// src/hmacAxios.ts
import axios, { AxiosInstance, AxiosRequestConfig, Method } from 'axios';
import { appConfig } from '../config/keycloak';

const BASE =
  appConfig.baseUrl;

const CLIENT_ID =
  'external';

const SECRET =
  'SGbsy1qTWxzPXuH7GSmCj7CcVzmkUXI4h8fMU0whf68=';

// ==== Utils crypto / encoding ====
const enc = new TextEncoder();

function toBytes(s: string) { return enc.encode(s); }

async function sha256Hex(bytes: Uint8Array | ArrayBuffer) {
  const buf = bytes instanceof Uint8Array ? bytes.buffer : bytes;
  const hash = await crypto.subtle.digest('SHA-256', buf as ArrayBuffer);
  const view = new Uint8Array(hash);
  // evitar for..of sobre TypedArray
  let out = '';
  for (let i = 0; i < view.length; i++) {
    out += view[i].toString(16).padStart(2, '0');
  }
  return out;
}

async function hmacBase64(secret: string, msg: string) {
  // pasar ArrayBuffer explícito a WebCrypto
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret).buffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const sigBuf = await crypto.subtle.sign('HMAC', key, enc.encode(msg).buffer);
  const sigView = new Uint8Array(sigBuf);

  // evitar for..of sobre TypedArray
  let bin = '';
  for (let i = 0; i < sigView.length; i++) {
    bin += String.fromCharCode(sigView[i]);
  }
  return btoa(bin);
}

function uuid() {
  // usa crypto.randomUUID si existe
  return (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random()}`;
}

function sortedQueryString(u: URL) {
  const entries: [string, string][] = [];
  u.searchParams.forEach((v, k) => entries.push([k, v]));
  entries.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1].localeCompare(b[1])));
  return entries.map(([k, v]) => `${k}=${v}`).join('&');
}

function buildUrl(path: string, params?: Record<string, any>): string {
  // Siempre construir URL absoluta (evita proxy que cambie host)
  const base = BASE || window.location.origin;
  const url = new URL(path, base);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach(val => url.searchParams.append(k, String(val)));
      else if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    });
    // Reordenar query (impacta en firma)
    const q = sortedQueryString(url);
    url.search = q ? `?${q}` : '';
  }
  return url.toString();
}

async function signRequest(method: Method, fullUrl: string, bodyBytes: Uint8Array) {
  const url = new URL(fullUrl);
  const contentSha = await sha256Hex(bodyBytes);
  const ts = Date.now().toString();
  const n = uuid();
  const signedHeaders = ['host', 'x-client-id', 'x-timestamp', 'x-nonce', 'content-sha256'];

  const canonical =
    `${String(method).toUpperCase()}\n` +
    `${url.pathname}\n` +
    `query=${sortedQueryString(url)}\n` +
    `host:${url.host}\n` +
    `x-client-id:${CLIENT_ID}\n` +
    `x-timestamp:${ts}\n` +
    `x-nonce:${n}\n` +
    `content-sha256:${contentSha}`;

  const sig = await hmacBase64(SECRET, canonical);

  return {
    'X-Client-Id': CLIENT_ID,
    'X-Timestamp': ts,
    'X-Nonce': n,
    'X-Signed-Headers': signedHeaders.join(';'),
    'Content-SHA256': contentSha,
    'X-Signature': sig,
  };
}

// Variante para multipart que incluye campos como "query params virtuales"
async function signRequestMultipart(
  method: Method, 
  fullUrl: string, 
  bodyBytes: Uint8Array,
  virtualParams: Record<string, string | string[]>
) {
  const url = new URL(fullUrl);
  const contentSha = await sha256Hex(bodyBytes);
  const ts = Date.now().toString();
  const n = uuid();
  const signedHeaders = ['host', 'x-client-id', 'x-timestamp', 'x-nonce', 'content-sha256'];

  // Construir query string virtual a partir de los campos multipart
  // El backend los ordena alfabéticamente
  const virtualUrl = new URL(fullUrl);
  Object.entries(virtualParams).forEach(([k, v]) => {
    if (Array.isArray(v)) {
      v.forEach(val => virtualUrl.searchParams.append(k, String(val)));
    } else if (v !== undefined && v !== null) {
      virtualUrl.searchParams.set(k, String(v));
    }
  });
  const virtualQuery = sortedQueryString(virtualUrl);

  const canonical =
    `${String(method).toUpperCase()}\n` +
    `${url.pathname}\n` +
    `query=${virtualQuery}\n` +
    `host:${url.host}\n` +
    `x-client-id:${CLIENT_ID}\n` +
    `x-timestamp:${ts}\n` +
    `x-nonce:${n}\n` +
    `content-sha256:${contentSha}`;

  const sig = await hmacBase64(SECRET, canonical);

  return {
    'X-Client-Id': CLIENT_ID,
    'X-Timestamp': ts,
    'X-Nonce': n,
    'X-Signed-Headers': signedHeaders.join(';'),
    'Content-SHA256': contentSha,
    'X-Signature': sig,
  };
}

// ==== Axios instance (sin transformaciones automáticas) ====
export const api: AxiosInstance = axios.create({
  baseURL: BASE, // debe ser absoluto al gateway para que 'host' coincida
  timeout: 60_000,
  // Evitar transformRequest por defecto en JSON
  transformRequest: [(data) => data],
});

// ==== JSON helper ====
// Enviamos string JSON *ya* serializado y firmamos esos bytes exactos
export async function hmacJson<T = any>(
  method: Method,
  path: string,
  body?: any,
  params?: Record<string, any>,
  extra?: AxiosRequestConfig
) {
  const full = buildUrl(path, params);
  const jsonStr = body !== undefined ? JSON.stringify(body) : '';
  const bytes = toBytes(jsonStr);
  const h = await signRequest(method, full, bytes);

  // Extraer headers de extra para fusionarlos correctamente
  const { headers: extraHeaders, ...restExtra } = extra || {};
  
  const headers = {
    'Content-Type': 'application/json',
    ...h,
    ...(extraHeaders || {}),
  };

  return api.request<T>({
    url: full, // ya viene con query ordenada
    method,
    data: jsonStr || undefined,
    headers,
    // No dejes que axios vuelva a serializar
    transformRequest: [(d) => d],
    ...restExtra,
  });
}

// ==== MULTIPART helper ====
// Construye el body multipart *exacto* y lo manda como Uint8Array/Blob (con boundary fijo)
type FilePart = {
  field: string;
  file: File | Blob;
  filename?: string;
  contentType?: string;
};

function boundaryString() {
  // estilo webkit para que sea familiar en inspecciones
  return '----WebKitFormBoundary' + Math.random().toString(36).slice(2, 15);
}

async function encodeTextPart(boundary: string, field: string, value: string) {
  const head =
    `--${boundary}\r\n` +
    `Content-Disposition: form-data; name="${field}"\r\n\r\n`;
  const tail = `\r\n`;
  const headBytes = toBytes(head);
  const valBytes = toBytes(value);
  const tailBytes = toBytes(tail);

  const out = new Uint8Array(headBytes.length + valBytes.length + tailBytes.length);
  out.set(headBytes, 0);
  out.set(valBytes, headBytes.length);
  out.set(tailBytes, headBytes.length + valBytes.length);
  return out;
}

async function encodeFilePart(boundary: string, part: FilePart) {
  const filename =
    part.filename ||
    (part.file as any).name ||
    `file-${Date.now()}`;
  const contentType =
    part.contentType ||
    (part.file as any).type ||
    'application/octet-stream';

  const head =
    `--${boundary}\r\n` +
    `Content-Disposition: form-data; name="${part.field}"; filename="${filename}"\r\n` +
    `Content-Type: ${contentType}\r\n\r\n`;
  const headBytes = toBytes(head);
  const fileBuf = new Uint8Array(await part.file.arrayBuffer());
  const tailBytes = toBytes(`\r\n`);

  const out = new Uint8Array(headBytes.length + fileBuf.length + tailBytes.length);
  out.set(headBytes, 0);
  out.set(fileBuf, headBytes.length);
  out.set(tailBytes, headBytes.length + fileBuf.length);
  return out;
}

function concatBytes(chunks: Uint8Array[]) {
  const total = chunks.reduce((sum, c) => sum + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  // iteramos sobre array normal (no TypedArray), ok para ES5
  for (let i = 0; i < chunks.length; i++) {
    const c = chunks[i];
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

export async function hmacMultipart<T = any>(
  path: string,
  fields: Record<string, string | string[]>,
  files: FilePart[] = [],
  params?: Record<string, any>,
  extra?: AxiosRequestConfig
) {
  // Para multipart/form-data, NO agregar params a la URL real
  // El backend construye el canonical string usando los campos del multipart como "query params virtuales"
  const full = buildUrl(path, undefined); // Sin query params en URL
  const boundary = boundaryString();

  const chunks: Uint8Array[] = [];
  // campos de texto (soporta arrays para campos múltiples)
  const fieldEntries = Object.entries(fields || {});
  for (let i = 0; i < fieldEntries.length; i++) {
    const [k, v] = fieldEntries[i];
    // Si es array, agregar múltiples campos con el mismo nombre
    if (Array.isArray(v)) {
      for (let j = 0; j < v.length; j++) {
        chunks.push(await encodeTextPart(boundary, k, v[j] ?? ''));
      }
    } else {
      chunks.push(await encodeTextPart(boundary, k, v ?? ''));
    }
  }
  // archivos
  for (let i = 0; i < files.length; i++) {
    chunks.push(await encodeFilePart(boundary, files[i]));
  }
  // cierre correcto (CRLF reales)
  chunks.push(toBytes(`--${boundary}--\r\n`));

  const bodyBytes = concatBytes(chunks);

  // Para multipart, el backend construye un "query string virtual" a partir de los campos
  // Necesitamos incluir esto en la firma HMAC
  const virtualQueryParams = { ...fields };
  
  // Firmar con el payload exacto, incluyendo los campos como query params virtuales
  const h = await signRequestMultipart('POST', full, bodyBytes, virtualQueryParams);

  const headers = {
    // Content-Type *con* boundary exacto
    'Content-Type': `multipart/form-data; boundary=${boundary}`,
    ...h,
    ...(extra?.headers || {}),
  };

  // Convertir Uint8Array a ArrayBuffer para que axios lo maneje correctamente
  // Esto NO altera el contenido, solo cambia el tipo de contenedor
  const bodyToSend = bodyBytes.buffer.slice(
    bodyBytes.byteOffset,
    bodyBytes.byteOffset + bodyBytes.byteLength
  );

  return api.request<T>({
    url: full,
    method: 'POST',
    data: bodyToSend,  // ArrayBuffer que axios puede enviar correctamente
    headers,
    responseType: 'json',
    ...extra,
  });
}
